/* @flow */
import React from 'react';
import {
    Paper,
    FormControl,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Divider,
    Grid,
    Button,
    Toolbar,
    Typography,
    IconButton,
    TextField,
} from '@mui/material';
import Dialog from '../../components/SharedComponents/Dialog/Dialog';
import HelpButton from '../../components/SharedComponents/HelpButton/HelpButton';
import backIcon from '../../assets/icons/back.png';
import AppLoader from '../../components/AppLoader';
import { getPermissionValue } from '../../helper-classes/utility-functions';
import { getFleetioAssetById, updateFleetioAssetSyncPreference } from './helper-classes/fleetio-services';
import { FLEETIO_MESSAGES, FLEETIO_HELP_TEXT } from './constants.fleetio';
import styles from './FleetioConfig.module.scss';

export type Props = {
    asset: Object,
    onCancel: Function,
    onSuccess: Function,
};

export type State = {
    loading: boolean,
    submitting: boolean,
    odometerSync: boolean,
    engineHourSync: boolean,
    dtcSync: boolean,
    dialogConfig: Object,
};

class FleetioAssetSyncConfig extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            loading: true,
            submitting: false,
            odometerSync: false,
            engineHourSync: false,
            dtcSync: false,
            dialogConfig: {
                open: false,
                type: '',
                customTitle: '',
                content: '',
                button: [],
            },
        };
    }

    componentDidMount() {
        const { asset } = this.props;
        getFleetioAssetById(asset.id)
            .then((data) => {
                if (data == null) return;
                this.setState({
                    odometerSync: data.odometerSync || false,
                    engineHourSync: data.engineHoursSync || false,
                    dtcSync: data.dtcSync || false,
                });
            })
            .finally(() => this.setState({ loading: false }));
    }

    handleCheckbox = (name: string) => {
        this.setState(prev => ({ [name]: !prev[name] }));
    }

    closeDialog = () => {
        this.setState({
            dialogConfig: {
                open: false,
                type: '',
                customTitle: '',
                content: '',
                button: [],
            },
        });
    }

    handleSubmit = () => {
        const { odometerSync, engineHourSync, dtcSync } = this.state;
        const { asset, onSuccess } = this.props;

        this.setState({ submitting: true });
        updateFleetioAssetSyncPreference(asset.id, {
            odometerSync,
            engineHoursSync: engineHourSync,
            dtcSync,
        })
            .then(() => {
                this.setState({
                    dialogConfig: {
                        open: true,
                        type: 'success',
                        customTitle: '',
                        content: FLEETIO_MESSAGES.ASSET_SYNC_UPDATE_SUCCESS,
                        button: [{
                            title: 'OK',
                            color: 'primary',
                            variant: 'contained',
                            handleClick: onSuccess,
                        }],
                    },
                });
            })
            .catch(() => {
                this.setState({
                    dialogConfig: {
                        open: true,
                        type: 'error',
                        customTitle: '',
                        content: FLEETIO_MESSAGES.ASSET_SYNC_SAVE_ERROR,
                        button: [{
                            title: 'OK',
                            color: 'primary',
                            variant: 'contained',
                            handleClick: this.closeDialog,
                        }],
                    },
                });
            })
            .finally(() => this.setState({ submitting: false }));
    }

    render() {
        const { onCancel, asset } = this.props;
        const {
            loading,
            submitting,
            odometerSync,
            engineHourSync,
            dtcSync,
            dialogConfig,
        } = this.state;
        const canModify = getPermissionValue('Admin') === 'Modify';
        const isBusy = loading || submitting;

        return (
            <div className={styles.app}>
                {isBusy && <AppLoader type="fullScreen" />}
                <Paper>
                    <Toolbar className={styles.toolbar}>
                        <span className={styles.backButton}>
                            <IconButton onClick={onCancel} size="large">
                                <img src={backIcon} alt="backArrow" />
                            </IconButton>
                        </span>
                        <Typography
                            variant="h6"
                            color="inherit"
                            className={styles.titleWrap}
                        >
                            Fleetio Integration
                        </Typography>
                    </Toolbar>
                    <div className={styles.form}>
                        <FormControl component="fieldset" className={styles.formControl}>
                            <FormGroup>
                                <Grid
                                    container
                                    direction="column"
                                    justifyContent="space-between"
                                    alignItems="stretch"
                                    className={styles.mainGrid}
                                >
                                    <Grid item xs={8}>
                                        <Typography className={styles.assetFieldTitle}>
                                            Asset
                                        </Typography>
                                        <TextField
                                            id="asset-id-field"
                                            className={styles.assetNameField}
                                            value={asset.name}
                                            inputProps={{ size: Math.max((asset.name || '').length, 1) }}
                                            margin="normal"
                                            disabled
                                        />
                                        <Typography className={styles.sectionTitle}>
                                            Sync Preferences
                                            <HelpButton
                                                type="syncPreference"
                                                helpText={FLEETIO_HELP_TEXT.ASSET_SYNC_PREFERENCE}
                                                popoverWidth={300}
                                            />
                                        </Typography>
                                        <div className={styles.checkboxRow}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={odometerSync}
                                                        onChange={() => this.handleCheckbox('odometerSync')}
                                                        color="primary"
                                                    />
                                                }
                                                label="Primary Meter (Odometer)"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={engineHourSync}
                                                        onChange={() => this.handleCheckbox('engineHourSync')}
                                                        color="primary"
                                                    />
                                                }
                                                label="Secondary Meter (Engine Hours)"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={dtcSync}
                                                        onChange={() => this.handleCheckbox('dtcSync')}
                                                        color="primary"
                                                    />
                                                }
                                                label="DTC (Vehicle Fault Codes)"
                                            />
                                        </div>
                                    </Grid>
                                </Grid>
                            </FormGroup>
                        </FormControl>
                        <Divider className={styles.divider} />
                        <Toolbar>
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                <Grid item>
                                    <Button
                                        className={styles.button}
                                        onClick={onCancel}
                                        color="primary"
                                    >
                                        Cancel
                                    </Button>
                                    {canModify && (
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            className={styles.primaryButton}
                                            onClick={this.handleSubmit}
                                            disabled={submitting}
                                        >
                                            Save
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </div>
                </Paper>
                <Dialog
                    open={dialogConfig.open}
                    type={dialogConfig.type}
                    customTitle={dialogConfig.customTitle}
                    button={dialogConfig.button}
                    content={dialogConfig.content}
                    maxWidth="sm"
                />
            </div>
        );
    }
}

export default FleetioAssetSyncConfig;

